<template>
  <q-btn-toggle
    v-model="dateSelection"
    class="border-grey-3 q-ma-none"
    size="md"
    unelevated
    :spread="!!activityId"
    toggle-color="white"
    toggle-text-color="primary"
    color="grey-4"
    text-color="dark"
    :options="dateOptions"
    :disable="disabled"
    @click="dateSelectionClickHandler"
  >
    <template #all>
      {{ $t('collection.all') }}
    </template>
    <template #today>
      {{ $t('time.today') }}
    </template>
    <template #tomorrow>
      {{ $t('time.tomorrow') }}
    </template>
    <template #calendar>
      <div
        ref="custom"
        class="row q-gutter-sm"
      >
        <q-icon
          name="calendar_today"
        />
        <div v-if="customDate">
          {{ customDateFormatted }}
        </div>
        <q-popup-proxy
          v-model="showCalendar"
          transition-show="scale"
          transition-hide="scale"
        >
          <q-date
            v-model="date"
            mask="YYYY-MM-DD"
            no-unset
            :options="formattedDatesWithShowing"
          />
        </q-popup-proxy>
      </div>
    </template>
  </q-btn-toggle>
</template>

<script>
import { date } from 'quasar'
const { formatDate, extractDate } = date

export default {
  name: 'ActivityDateToggle',
  configuration: ['currentSite'],
  props: {
    activityId: {
      type: Number,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    initialDate: {
      type: String,
      default: null
    },
    showAllOption: {
      type: Boolean,
      default: false
    }
  },
  emits: ['select-date'],
  data () {
    const initialDateFormatted = this.initialDate?.replace(/\//g, '-')
    let initialDateSelection
    if (initialDateFormatted) {
      if (initialDateFormatted === this.currentSite.businessDate) {
        initialDateSelection = 'today'
      } else if (initialDateFormatted === this.currentSite.businessTomorrow) {
        initialDateSelection = 'tomorrow'
      } else {
        initialDateSelection = 'custom'
      }
    }

    return {
      today: this.currentSite.businessDate,
      tomorrow: this.currentSite.businessTomorrow,
      date: initialDateFormatted || this.currentSite.businessDate,
      dateSelection: initialDateSelection,
      showCalendar: false,
      customDate: null
    }
  },
  computed: {
    dateOptions () {
      const dateOptions = [{ value: 'today', slot: 'today' }]

      if (_.isNil(this.datesWithActivities.value)) {
        return dateOptions
      }

      if (this.datesWithActivities.value.includes(this.tomorrow)) {
        dateOptions.push({ value: 'tomorrow', slot: 'tomorrow' })
      }

      const customDates = _.filter(this.datesWithActivities.value, date => date !== this.today && date !== this.tomorrow)
      if (customDates.length > 0) {
        dateOptions.push({ value: 'custom', slot: 'calendar' })
      }

      if (this.showAllOption) {
        dateOptions.push({ value: 'all', slot: 'all' })
      }

      return dateOptions
    },
    formattedDatesWithShowing () {
      return this.datesWithActivities.value?.map(date => date.replace(/-/g, '/')) || []
    },
    customDateFormatted () {
      return formatDate(extractDate(this.date + ' 00:00', 'YYYY-MM-DD HH:mm'), 'MMM DD')
    }
  },
  watch: {
    date: {
      handler () {
        this.showCalendar = false
        this.validateDateSelection()
      },
      immediate: true
    },
    dateSelection () {
      if (this.dateSelection === 'all') {
        this.date = null
        this.$trackEvent(this.$customerEvents.ACTIVITY_SESSIONS_SELECTED_ALL)
      } else if (this.dateSelection === 'today') {
        this.date = this.today
        this.$trackEvent(this.$customerEvents.ACTIVITY_SESSIONS_SELECTED_TODAY)
      } else if (this.dateSelection === 'tomorrow') {
        this.date = this.tomorrow
        this.$trackEvent(this.$customerEvents.ACTIVITY_SESSIONS_SELECTED_TOMORROW)
      } else if (this.dateSelection === 'custom') {
        this.validateDateSelection()
        this.$trackEvent(this.$customerEvents.ACTIVITY_SESSIONS_SELECTED_CUSTOM)
      }
    },
    dateOptions () {
      const noActivitySessionsTodayOrTomorrow =
        _(this.dateOptions).map('value').find(value => ['today', 'tomorrow'].includes(value)) === undefined

      if (noActivitySessionsTodayOrTomorrow) {
        this.dateSelection = 'custom'
        this.customDate = true
        this.date = this.datesWithActivities.value?.[0]
      }
    }
  },
  mounted () {
    this.validateDateSelection()
  },
  methods: {
    dateSelectionClickHandler (event) {
      if (!this.$refs.custom) return

      if (
        this.$refs.custom === event.target ||
        this.$refs.custom.contains(event.target) ||
        event.target.contains(this.$refs.custom)) {
        this.showCalendar = true
      }
    },
    validateDateSelection () {
      if (this.date === this.today) {
        this.dateSelection = 'today'
        this.customDate = false
      } else if (this.date === this.tomorrow) {
        this.dateSelection = 'tomorrow'
        this.customDate = false
      } else if (this.date === null) {
        this.dateSelection = 'all'
        this.customDate = false
      } else {
        this.dateSelection = 'custom'
        this.customDate = true
      }

      this.$emit('select-date', this.date)
    },
    clearDate () {
      this.dateSelection = 'all'
      this.date = null
      this.customDate = false
      this.$emit('select-date', this.date)
    }
  },
  models: {
    datesWithActivities: {
      variables () {
        return {
          activity_id: this.activityId
        }
      },
      immediate: true,
      force: true
    }
  }
}
</script>

<style lang="scss" scoped>
.border-grey-3 {
  border: 3px solid $grey-4;
}
</style>
